'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string;
  role: 'admin' | 'accountant' | 'staff';
  is_active: boolean;
}

interface Company {
  id: string;
  name: string;
  legal_name: string;
  gstin: string;
  gst_type: string;
  state: string;
  invoice_prefix: string;
  is_active: boolean;
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  profile: UserProfile | null;
  activeCompany: Company | null;
  companies: Company[];
  companyRole: string | null;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  setActiveCompany: (company: Company) => void;
  refreshCompanies: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activeCompany, setActiveCompanyState] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyRole, setCompanyRole] = useState<string | null>(null);
  const supabase = createClient();
  const router = useRouter();

  const loadProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setProfile(data as UserProfile);
      }
    } catch {}
  };

  const loadCompanyRole = async (userId: string, companyId: string) => {
    try {
      // Check if owner
      const { data: company } = await supabase
        .from('companies')
        .select('owner_id')
        .eq('id', companyId)
        .single();

      if (company?.owner_id === userId) {
        setCompanyRole('admin');
        return;
      }

      // Check company_members
      const { data: member } = await supabase
        .from('company_members')
        .select('role')
        .eq('company_id', companyId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .single();

      if (member) {
        setCompanyRole(member.role);
      } else {
        // Fallback to profile role
        const { data: prof } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', userId)
          .single();
        setCompanyRole(prof?.role || 'staff');
      }
    } catch {
      setCompanyRole('staff');
    }
  };

  const loadCompanies = async (userId: string) => {
    try {
      const { data: ownedCompanies } = await supabase
        .from('companies')
        .select('*')
        .eq('owner_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      const { data: memberCompanies } = await supabase
        .from('company_members')
        .select('companies(*)')
        .eq('user_id', userId)
        .eq('is_active', true);

      const memberList = (memberCompanies || [])
        .map((m: any) => m.companies)
        .filter(Boolean)
        .filter((c: any) => c.is_active);

      const allCompanies = [
        ...(ownedCompanies || []),
        ...memberList.filter(
          (mc: any) => !(ownedCompanies || []).find((oc: any) => oc.id === mc.id)
        ),
      ] as Company[];

      setCompanies(allCompanies);

      const stored = typeof window !== 'undefined' ? localStorage.getItem('kitchenly_active_company')
        : null;
      if (stored) {
        const found = allCompanies.find((c) => c.id === stored);
        if (found) {
          setActiveCompanyState(found);
          await loadCompanyRole(userId, found.id);
          return;
        }
      }
      if (allCompanies.length > 0) {
        setActiveCompanyState(allCompanies[0]);
        if (typeof window !== 'undefined') {
          localStorage.setItem('kitchenly_active_company', allCompanies[0].id);
        }
        await loadCompanyRole(userId, allCompanies[0].id);
      }
    } catch {}
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
        loadCompanies(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
        loadCompanies(session.user.id);
      } else {
        setProfile(null);
        setCompanies([]);
        setActiveCompanyState(null);
        setCompanyRole(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'admin',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    router.refresh();
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setCompanies([]);
    setActiveCompanyState(null);
    setCompanyRole(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('kitchenly_active_company');
    }
    router.push('/login');
    router.refresh();
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data as UserProfile;
  };

  const setActiveCompany = (company: Company) => {
    setActiveCompanyState(company);
    if (typeof window !== 'undefined') {
      localStorage.setItem('kitchenly_active_company', company.id);
    }
    if (user) {
      loadCompanyRole(user.id, company.id);
    }
  };

  const refreshCompanies = async () => {
    if (user) await loadCompanies(user.id);
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    profile,
    activeCompany,
    companies,
    companyRole,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    setActiveCompany,
    refreshCompanies,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };